import React from "react";
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Typography,
  darken,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IconButton } from "@mui/material";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import { FieldsGroup } from "./FieldsGroup";
import { ReactComponent as DeleteIcon } from "../../icons/Bin.svg";
import { TagDto } from "@growth-machine-llc/stridist-api-client";
import TagChip from "../exercise-library/tags/TagChip";
import { exerciseTagCategoryMultiSelectStyles } from "../exercise-library/exerciseLibraryStyles";
import ListboxTagWithFooterComponent from "../settings/ListBoxTagFooterComponent";
import { AddNewItemButton } from "../workout/AddNewItemButton";
import { ManageTagDialog } from "../exercise-library/tags/ManageTagDialog";
import { AddedLozenge } from "../item/AddedLozenge";
import DoneIcon from "@mui/icons-material/Done";
import { isEqual } from "lodash";
import { ClientTagBadge } from "../coach-clients-tags/ClientTagBadge";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    margin: 0,
  },

  popper: {
    zIndex: "99999 !important" as any,
    borderRadius: 12,
    boxShadow: theme.shadows[5],
  },

  option: {
    padding: theme.spacing(1, 2),
    fontSize: 18,
    fontWeight: 500,
  },
  renderLabel: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    paddingInline: 12,
    paddingBlock: 8,
    "&:hover": {
      backgroundColor: darken(theme.palette.common.white, 0.1),
      boxShadow: "none",
    },
  },
  icon: {
    padding: 4,
    marginLeft: 4,
  },
  tag: {
    padding: theme.spacing(0.5, 1.5),
    height: 22,
    "& > *": {
      fontSize: 14,
    },
    alignItems: "center",
  },
  listbox: {
    boxShadow: "none",
  },
  badge: {
    "& .MuiBadge-badge": {
      top: 2,
      right: 2,
      height: 17,
    },
  },
  tagContainer: {
    "&.MuiBox-root": {
      marginRight: theme.spacing(1),
    },
  },
  noOptions: {
    padding: 0,
  },
}));

export interface TagFieldProps
  extends Omit<
    AutocompleteProps<any, true, false, any>,
    "options" | "onChange"
  > {
  onChange?: (data: string) => void;
  allTags?: TagDto[];
  initialSelectedTags?: TagDto[];
  onDeleteTag: (id: number, title: string) => void;
  disabled: boolean;
  manageTags?: () => void;
  onChangeSelectedOptions?: (tags: TagDto[]) => void;
}

const MAX_VISIBLE_TAGS = 2;

const TagField = (props: TagFieldProps) => {
  const {
    onChange,
    allTags,
    onDeleteTag,
    disabled,
    manageTags,
    initialSelectedTags,
    onChangeSelectedOptions,
    ...other
  } = props;
  const s = useStyles();
  const classes = exerciseTagCategoryMultiSelectStyles();

  const { breakpoints } = useTheme();
  const betweenSmAndLlg = useMediaQuery(breakpoints.between("sm", "llg"));

  const MAX_VISIBLE_TAGS = betweenSmAndLlg ? 1 : 3;

  const [selectedOptions, setSelectedOptions] =
    React.useState<TagDto[]>(initialSelectedTags);

  React.useEffect(() => {
    if (!isEqual(selectedOptions, initialSelectedTags)) {
      setSelectedOptions(initialSelectedTags);
    }
  }, [initialSelectedTags]);

  const toggleTag = (tag: TagDto) => {
    const updated = isSelected(tag)
      ? selectedOptions.filter((t) => t.id !== tag.id)
      : [...selectedOptions, tag];

    setSelectedOptions(updated);
    onChangeSelectedOptions?.(updated);
  };

  const isSelected = (tag: TagDto) =>
    selectedOptions.some((t) => t.id === tag.id);

  const renderLabel = (others, tag: TagDto, ownerState) => {
    return (
      <Box
        key={`box-${tag.id}`}
        className={classes.tagOption}
        onClick={() => toggleTag(tag)}
      >
        <Box
          sx={{
            width: 24,
            display: "inline-flex",
            justifyContent: "center",
            mr: 1,
          }}
        >
          {isSelected(tag) && <DoneIcon color="primary" fontSize="small" />}
        </Box>
        <Box>
          <TagChip
            primaryColor={tag.primaryColor}
            className={s.tag}
            text={tag.title}
          />
          <Typography className={classes.tagDescription}>
            {tag.description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderListbox = React.useCallback(
    (props) => (
      <ListboxTagWithFooterComponent
        {...props}
        onlySelectAllowed={false}
        manageTags={manageTags}
        disableAddNewTag={false}
      />
    ),
    [manageTags],
  );

  return (
    <>
      <FieldsGroup className={clsx(s.root)} label={"Tag #"}>
        <Autocomplete
          classes={{
            root: clsx(s.root),
            paper: s.paper,
            popper: s.popper,
            option: s.option,
            listbox: s.listbox,
            noOptions: s.noOptions,
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.title
          }
          multiple
          fullWidth
          options={allTags}
          value={selectedOptions}
          renderOption={renderLabel}
          renderTags={(records: TagDto[], _1) => {
            const MAX_TOTAL_LENGTH = 20;

            let totalLength = 0;
            const visibleTags: JSX.Element[] = [];
            let visibleCount = 0;
            let hiddenCount = 0;

            for (const value of records) {
              const tagLength = value.title.trim().length;

              if (
                totalLength + tagLength > MAX_TOTAL_LENGTH ||
                visibleCount >= MAX_VISIBLE_TAGS
              ) {
                hiddenCount++;
                continue;
              }

              totalLength += tagLength;
              visibleCount++;

              visibleTags.push(
                <TagChip
                  key={value.id}
                  text={value.title}
                  primaryColor={value.primaryColor}
                  className={clsx(s.tagContainer)}
                />,
              );
            }

            return (
              <>
                {visibleTags}
                {hiddenCount > 0 && `+${hiddenCount}`}
              </>
            );
          }}
          noOptionsText={
            <AddNewItemButton
              handleClick={manageTags}
              disabled={false}
              text="+ Add new client tag"
            />
          }
          slots={{
            listbox: renderListbox,
          }}
          disabled={disabled}
          {...other}
        />
      </FieldsGroup>
    </>
  );
};

export default TagField;
